import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Mail, KeyRound, Lock, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Two-step reset UI:
 *   1) email + 6-digit code  →  POST /api/auth/password-reset/verify → reset_token
 *   2) new password + confirm →  POST /api/auth/password-reset/confirm { reset_token, new_password }
 */
export default function ResetPassword() {
  const nav = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState(location.state?.email || "");
  const [code, setCode] = useState("");
  const [resetToken, setResetToken] = useState(null);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const codeInputRef = useRef(null);

  useEffect(() => {
    if (step === 1 && email) codeInputRef.current?.focus();
  }, [step, email]);

  const verify = async () => {
    if (!email.trim() || code.length !== 6) {
      toast.error("Enter your email and 6-digit code"); return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/api/auth/password-reset/verify", {
        email: email.trim(), code: code.trim(),
      });
      setResetToken(data.reset_token);
      setStep(2);
      toast.success("Code accepted — set your new password");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verification failed");
    } finally { setBusy(false); }
  };

  const confirm = async () => {
    if (pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (pw !== pw2) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      await api.post("/api/auth/password-reset/confirm", {
        reset_token: resetToken, new_password: pw,
      });
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="reset-password-page">
      <Header />
      <main id="main-content" className="flex-1 max-w-[500px] w-full mx-auto px-4 sm:px-6 py-12">
        <div className="card-surface p-6 sm:p-7 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none opacity-40"
               style={{ background: "radial-gradient(circle at 100% 0%, rgba(0,255,156,0.14), transparent 60%)" }} />
          <div className="relative">
            <div className="label-mono">
              Reset password · Step {step} of {step === 3 ? 3 : 2}
            </div>

            {step === 1 && (
              <>
                <h1 className="text-3xl text-white font-semibold tracking-tight mt-1">Enter your code</h1>
                <p className="text-white/60 text-[13px] mt-2 leading-relaxed">
                  Paste the 6-digit code we sent to your email. It expires in 5 minutes.
                </p>
                <div className="mt-5 space-y-3">
                  <div>
                    <label className="label-mono">Email</label>
                    <div className="relative mt-1">
                      <Mail className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                      <input type="email" value={email}
                             onChange={(e) => setEmail(e.target.value)}
                             className="input-glass w-full pl-8"
                             data-testid="reset-email-input" />
                    </div>
                  </div>
                  <div>
                    <label className="label-mono">6-digit code</label>
                    <div className="relative mt-1">
                      <KeyRound className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                      <input
                        ref={codeInputRef}
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]{6}"
                        maxLength={6}
                        value={code}
                        onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                        onKeyDown={(e) => e.key === "Enter" && verify()}
                        placeholder="123456"
                        className="input-glass w-full pl-8 font-mono tracking-[0.35em] text-center text-lg"
                        data-testid="reset-code-input"
                      />
                    </div>
                  </div>
                  <button onClick={verify} disabled={busy || code.length !== 6}
                          className="btn-accent w-full inline-flex items-center justify-center gap-2"
                          data-testid="reset-verify-btn">
                    {busy ? "Verifying…" : "Verify code"} <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                  <div className="text-center label-mono">
                    <Link to="/forgot-password" className="hover:text-white transition-colors">
                      ← Request a new code
                    </Link>
                  </div>
                </div>
              </>
            )}

            {step === 2 && (
              <>
                <h1 className="text-3xl text-white font-semibold tracking-tight mt-1">Create new password</h1>
                <p className="text-white/60 text-[13px] mt-2 leading-relaxed">
                  Choose a strong password of at least 6 characters. You'll be signed in fresh with the new password.
                </p>
                <div className="mt-5 space-y-3">
                  <div>
                    <label className="label-mono">New password</label>
                    <div className="relative mt-1">
                      <Lock className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                      <input type="password" value={pw}
                             onChange={(e) => setPw(e.target.value)}
                             className="input-glass w-full pl-8"
                             autoComplete="new-password" minLength={6}
                             data-testid="new-password-input" />
                    </div>
                  </div>
                  <div>
                    <label className="label-mono">Confirm password</label>
                    <div className="relative mt-1">
                      <Lock className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                      <input type="password" value={pw2}
                             onChange={(e) => setPw2(e.target.value)}
                             onKeyDown={(e) => e.key === "Enter" && confirm()}
                             className="input-glass w-full pl-8"
                             autoComplete="new-password" minLength={6}
                             data-testid="confirm-password-input" />
                    </div>
                  </div>
                  <button onClick={confirm}
                          disabled={busy || pw.length < 6 || pw !== pw2}
                          className="btn-accent w-full inline-flex items-center justify-center gap-2"
                          data-testid="reset-confirm-btn">
                    {busy ? "Saving…" : "Save new password"} <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </>
            )}

            {step === 3 && (
              <div data-testid="reset-success-state">
                <div className="flex items-center gap-2 mt-2">
                  <CheckCircle2 className="w-5 h-5 text-[var(--bull)]" />
                  <h1 className="text-2xl text-white font-semibold tracking-tight">
                    Password reset
                  </h1>
                </div>
                <p className="text-white/70 text-[13px] mt-2 leading-relaxed">
                  Your password has been updated. Sign in with your new password to continue.
                </p>
                <button
                  onClick={() => nav("/login")}
                  className="btn-accent w-full inline-flex items-center justify-center gap-2 mt-5"
                  data-testid="reset-goto-login-btn"
                >
                  Sign in <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
